import { useCallback, useMemo, useState, useTransition } from "react";

import { useDeepCompareEffect, useInfiniteScroll, useSyncedRef } from "@skydio/rivet-ui";

import { useConnectionNodes } from "../connections";

import type { KeyType } from "react-relay/relay-hooks/helpers";
import type { usePaginationFragmentHookType } from "react-relay/relay-hooks/usePaginationFragment";
import type { Options } from "react-relay/relay-hooks/useRefetchableFragmentNode";
import type { OperationType, VariablesOf } from "relay-runtime";
import type { UseInfiniteScrollProps } from "@skydio/rivet-ui";
import type { Connection, Edge } from "../connections";

/**
 * Takes the output of a Relay pagination fragment and returns the necessary data to render an
 * infinite scroll UI. Handles convenience features like unwrapping the connection data
 * (transforming it into a flat array of nodes) and refetching the fragment when variables change.
 *
 * @param paginationData The output of a `usePaginationFragment` hook
 * @param getConnection A function that extracts the connection data from the fragment data
 * @param pageSize The number of items to display per page
 * @param variables (Optional) The variables to use when refetching the fragment
 * @param infiniteScrollProps (Optional) Props to pass to the `useInfiniteScroll` hook
 * @param refetchOptions (Optional) Options to pass to the "refetch" fucntion when variables change
 *
 * @returns A ref to the loader element (returned from `useInfiniteScroll`)
 * @returns A ref to the scroller element (returned from `useInfiniteScroll`)
 * @returns The connection data from the fragment - this will be unwrapped via `useConnectionNodes`
 * @returns A boolean indicating whether the fragment is currently refetching
 * @returns A boolean indicating whether the fragment is currently loading more data
 */
export const usePaginationInfiniteScroll = <
  TQuery extends OperationType,
  TData,
  TKey extends KeyType<TData>,
  TConnectionData,
  ScrollContainerElement extends HTMLElement = HTMLDivElement,
  LoaderElement extends HTMLElement = HTMLDivElement,
>(
  paginationData: usePaginationFragmentHookType<TQuery, TKey, TData>,
  getConnection: (data: TData) => Connection<Edge<TConnectionData>> | null | undefined,
  pageSize = 10,
  variables: Partial<VariablesOf<TQuery>> = {},
  infiniteScrollProps: Omit<UseInfiniteScrollProps, "hasMore" | "loadMore"> = {},
  refetchOptions: Options = {}
) => {
  const { data, hasNext, loadNext, refetch, isLoadingNext } = paginationData;

  const getConnectionRef = useSyncedRef(getConnection);

  const connection = useMemo(() => {
    return getConnectionRef.current(data);
  }, [getConnectionRef, data]);

  const nodes = useConnectionNodes(connection);

  const loadMore = useCallback(() => {
    loadNext(pageSize);
  }, [loadNext, pageSize]);

  const [loaderRef, scrollerRef] = useInfiniteScroll<ScrollContainerElement, LoaderElement>({
    ...infiniteScrollProps,
    hasMore: hasNext,
    onLoadMore: loadMore,
  });

  const [isRefetching, startTransition] = useTransition();

  const emptyPaginationData = paginationData.data == null;

  // Refetch the fragment when variables change or on mount if variables differ from the
  // query owner's. This ensures data stays fresh after component remounts (e.g. stack
  // navigation resets) where the initial query's connection may be stale.
  useDeepCompareEffect(() => {
    if (!emptyPaginationData) {
      startTransition(() => {
        refetch(variables, refetchOptions);
      });
    }
  }, [variables, emptyPaginationData, refetchOptions]);

  return [loaderRef, scrollerRef, nodes, isRefetching, isLoadingNext, loadMore] as const;
};

/**
 * A specialized version of `usePaginationInfiniteScroll` for use with data entry components that
 * use a listbox within a popover; i.e. Select, ComboBox, and SelectFilter.
 *
 * @param paginationData The output of a `usePaginationFragment` hook
 * @param getConnection A function that extracts the connection data from the fragment data
 * @param pageSize The number of items to display per page
 * @param variables (Optional) The variables to use when refetching the fragment
 *
 * @returns Props to spread onto the data entry component
 * @returns The connection data from the fragment - this will be unwrapped via `useConnectionNodes`
 */
export const usePopoverPaginationInfiniteScroll = <
  TQuery extends OperationType,
  TData,
  TKey extends KeyType<TData>,
  TConnectionData,
>(
  paginationData: usePaginationFragmentHookType<TQuery, TKey, TData>,
  getConnection: (data: TData) => Connection<Edge<TConnectionData>> | null | undefined,
  pageSize = 10,
  variables: Partial<VariablesOf<TQuery>> = {}
) => {
  const [isOpen, setIsOpen] = useState(false);

  const [loaderRef, scrollRef, nodes, isRefetching, isLoadingNext, loadMore] =
    usePaginationInfiniteScroll(paginationData, getConnection, pageSize, variables, {
      isEnabled: isOpen,
    });

  const listboxProps = {
    scrollRef,
    onOpenChange: setIsOpen as () => void,
    isLoading: isRefetching,
    listboxProps: {
      isLoading: paginationData.hasNext,
      loaderRef: loaderRef,
    },
  };

  return [listboxProps, nodes, isLoadingNext, loadMore] as const;
};
